import { Responsible, BasicMemberInfo, CostCenterItem, CustomerInvoiceRow, DraftToSave, PrepaidCompetitionSyncData, ReconciliationStatus, EventorInvoiceImportData, ExpensesData, IncomesData, ListEventData, OverviewData, BudgetStatus, ProjectWithArticles, ProjectWithIncomeAccounts, Account, Supplier, Reimbursee, SwishSettingsData, SwishCategoryMapping, SwishMessageMapping, SwishLocation, SwishCostLocation, SwishSyncPreview, SyncRail, ServiceRow, InvoiceSplit } from "./Types"
import { Capability } from "./generated/capabilities"
import { reportError } from "./CallCenter"

// Every non-OK response carries one uniform shape: the backend always answers
// {"error": code, "message": text} + an HTTP status (see web/app.py). We carry
// that through as an ApiError so the app can dispatch on it, and report it to the
// error bus so nothing is ever silently swallowed.
export type ApiError = { status: number, code: string, message: string, vismaCode?: number, vismaRef?: string }

export function isApiError(error: unknown): error is ApiError {
    return typeof error === "object" && error !== null
        && "status" in error && typeof error.status === "number"
        && "code" in error && typeof error.code === "string"
        && "message" in error && typeof error.message === "string"
}

async function parseResponse(response: Response) {
    if (!response.ok) {
        const body = await response.json().catch(() => undefined)
        const error: ApiError = {
            status: response.status,
            code: typeof body?.error === "string" ? body.error : "request_failed",
            message: typeof body?.message === "string" ? body.message : "Något gick fel.",
            vismaCode: typeof body?.visma_code === "number" ? body.visma_code : undefined,
            vismaRef: typeof body?.visma_ref === "string" ? body.visma_ref : undefined,
        }
        reportError(error)
        throw error
    }
    return await response.json()
}

async function doFetch(url: string, method: 'POST' | 'PUT' | 'DELETE', data: object) {
    const response = await fetch(
        url,
        {
            "method": method,
            "headers": { "Content-Type": "application/json" },
            "body": JSON.stringify(data),
            // Let a mutation dispatched during page unload (the beforeunload flush in CallCenter)
            // still complete after the page goes away. Bodies here are small, well under the 64 KB
            // keepalive limit.
            "keepalive": true
        }
    )
    return await parseResponse(response)
}

async function post(url: string, data: object) {
    return await doFetch(url, 'POST', data)
}

async function put(url: string, data: object) {
    return await doFetch(url, 'PUT', data)
}

async function del(url: string, data: object) {
    return await doFetch(url, 'DELETE', data)
}

async function get(url: string, headers?: Record<string, string>) {
    const response = await fetch(url, headers === undefined ? undefined : { headers })
    return await parseResponse(response)
}


// A domestic supplier is reached by giro; a foreign one (currency other than SEK) by IBAN/BIC —
// which the backend pairs with the club's configured pay-from account.
// `uses_ocr` marks the supplier as one whose invoices carry an OCR number. Visma insists that
// this agrees with the invoice in both directions, and the backend now matches it to whatever
// invoice is being registered — so this is a starting value, not the safeguard.
export const apiCreateSupplier = async (data:
        {name: string, giro_number: string, currency_code?: string, uses_ocr?: boolean}
      | {name: string, clearing_number: string, bank_account_number: string, currency_code?: string}
      | {name: string, currency_code: string, iban: string, bic: string,
         address_1: string, postal_code: string, city: string, country_code: string,
         payment_code: number, uses_ocr?: boolean}) =>
    post('/expenses/supplier', data) as Promise<{suppliers: Supplier[], new_supplier_id: string}>

// Skatteverket's betalningskoder. Fetched only when a foreign supplier is being created — ~96
// entries, irrelevant to a domestic one. No default: the code says what the payment is for.
export type ForeignPaymentCodes = { codes: { code: number, description: string }[] }
export const apiGetForeignPaymentCodes = async () =>
    get('/expenses/foreign-payment-codes') as Promise<ForeignPaymentCodes>

// What the document itself states, so the form can offer it instead of making the user retype it.
// Every field is optional and an empty object is an ordinary answer — a photographed document has
// no text to read, and the user then fills the form in as before.
//
// The payment details each carry a checksum and are proved before being offered; the invoice
// fields carry none and rest on having been printed beside a label. `asks_to_be_paid` says the
// document names an account to send money to, which is how a document Visma never classified is
// told from an expense receipt.
export type DocumentReading = {
    giro?: string, iban?: string, bic?: string, ocr_reference?: string,
    amount?: number, invoice_date?: string, due_date?: string, invoice_number?: string,
    asks_to_be_paid?: boolean,
}
export const apiReadDocument = async (attachmentId: string) =>
    get(`/expenses/read-document/${attachmentId}`) as Promise<DocumentReading>

// Visma's own country list, Swedish names, already in Swedish alphabetical order. Used for a
// foreign supplier's country, which Visma stores as a code.
export type Countries = { countries: { code: string, name: string }[] }
export const apiGetCountries = async () =>
    get('/expenses/countries') as Promise<Countries>

// Either the bank account (clearing + number) or the giro number, dash included; the other null.
export const apiCreateReimbursee = async (data: {name: string, clearing_number: string | null,
                                                bank_account_number: string | null, giro_number: string | null}) =>
    post('/expenses/reimbursee', data) as Promise<{reimbursees: Reimbursee[], new_reimbursee_id: string}>

export const apiCreateCustomerInvoice = async (data: {customer_id: string, rows: CustomerInvoiceRow[], final_text: string}) =>
    post('/incomes/create/react', data) as Promise<{}>

export const apiGetIncomesData = async () => get('/incomes/create/react/everything') as Promise<IncomesData>

export const apiCreateArticle = async (data: {name: string, standard_price: number, income_account: number, project_id: string, project_number: string}) =>
    post('/incomes/articles', data) as Promise<{new_article_id: string, cost_center_items: CostCenterItem<ProjectWithArticles>[]}>

export const apiGetEventorInvoiceImportData = async () => get('/incomes/eventor') as Promise<EventorInvoiceImportData>

export const apiGetEventorEventInvoices = async (eventId: number) =>
    get(`/incomes/eventor/invoices?event_id=${eventId}`) as Promise<ListEventData>

// Invoices already read, taken apart again — with a late surcharge stated after the reading — from
// the lines the preview kept, without reading Eventor's pages again.
export const apiResplitEventorInvoices = async (body: {
        event_id: number, late_surcharge_percent: number | null,
        invoices: { invoice_number: number, total_amount: number, lines: { text: string, amount: number }[] }[] }) =>
    post('/incomes/eventor/invoice-splits/resplit', body) as Promise<{ splits: InvoiceSplit[] }>

// Started, not awaited: the run takes minutes. `run_id` is how it is asked after.
export const apiStartPrepaidCompetitionReconciliation = async (
        payment_event_id: number, event_id: number | null) =>
    post(`/incomes/prepaid-competition/reconciliation?payment_event_id=${payment_event_id}`
         + `${event_id === null ? '' : `&event_id=${event_id}`}`, {}
    ) as Promise<{ run_id: string }>

// 'latest' asks for this person's most recent run, so a reloaded page finds its way back to one
// still going or already finished.
export const apiGetPrepaidCompetitionReconciliation = async (run_id: string) =>
    get(`/incomes/prepaid-competition/reconciliation/${encodeURIComponent(run_id)}`
    ) as Promise<ReconciliationStatus>

export const apiSetPrepaidHideInSync = async (value: boolean) =>
    put('/incomes/prepaid-competition/hide-in-sync', {value}) as Promise<{}>

// The club's rows, and the competition's whole service menu with them: both are on the one page
// Eventor renders, so the list a correction may have to choose from costs no second fetch.
export const apiGetPrepaidServices = async (event_id: number, club_id: number) =>
    get(`/incomes/prepaid-competition/services?event_id=${event_id}&club_id=${club_id}`) as Promise<{
        // `refusal` is the server's own words for why this club cannot be corrected (null when it
        // can): the same sentence the write would raise, so the view never phrases it twice.
        rows: ServiceRow[], services: { id: number, name: string }[], refusal: string | null }>

// Eko's only destructive write into Eventor. `quantity` is what the club should be left holding,
// zero meaning the row goes. The reply is what it still has, read back from Eventor rather than
// assumed — the server does not refuse a change its own interface would. It also orders what
// survives and credits what was billed; that order is filed under our own club, which is why the
// reply carries its reference number.
export const apiCorrectPrepaidServices = async (
        payment_event_id: number, club_id: number, bill_difference: boolean,
        changes: { request_id: number, quantity: number }[],
        // The name of a 0 kr service in the competition, needed only when every row goes: there is
        // then nothing to re-order, so nothing raises the invoice the credit has to be written on.
        change_service: string | null) =>
    post('/incomes/prepaid-competition/services/correct',
         { payment_event_id, club_id, bill_difference, changes, change_service }) as Promise<{
        rows: ServiceRow[], order_reference: number | null, order_amount_ore: number }>

// Every club with an entry, plus the competition the payment event resolved to (kept per process,
// since finding it means asking every event the organisation administers).
export const apiGetPrepaidClubs = async (payment_event_id: number, event_id?: number) =>
    get(`/incomes/prepaid-competition/clubs?payment_event_id=${payment_event_id}`
        + (event_id === undefined ? '' : `&event_id=${event_id}`)) as Promise<{
        // `candidates` is non-empty only when the payment event covers more than one competition,
        // and then the clubs are empty until one is chosen.
        event_id: number | null, clubs: { id: number, name: string }[], candidates: number[] }>

export const apiGetPrepaidCompetitionSync = async (payment_event_id: number) =>
    get(`/incomes/prepaid-competition/sync?payment_event_id=${payment_event_id}`) as Promise<PrepaidCompetitionSyncData>

// --- PrepaidCompetition config (formerly eventor_25manna.json): the due date + the price→location map. The
// payment event is picked live in the sync view (not stored). ---
export type PaymentEvent = { id: number, name: string }
export type PrepaidCompetitionScalars = { due_date: string }
// One part of a price: how much of it books here, and where. A price with a single part worth the
// whole price is the ordinary case; several parts say that one figure on the Eventor order is more
// than one thing — a late entry of 9 250 kr is an 8 520 kr entry fee plus a 730 kr late charge.
export type PrepaidCompetitionPricePart = {
    amount: number, income_account: number, cost_center_item_id: string, project_id: string }
export type PrepaidCompetitionPriceLocation = {
    unit_price: number, parts: PrepaidCompetitionPricePart[] }
export type PrepaidCompetitionConfigData = {
    configured: boolean,
    scalars: PrepaidCompetitionScalars | null,
    price_locations: PrepaidCompetitionPriceLocation[],
    cost_center_items: CostCenterItem<ProjectWithIncomeAccounts>[],
    official_income_accounts: Account[],
    // Complete GUID -> name maps so a stored price row outside every role's scope still resolves.
    cost_center_names: Record<string, string>,
    project_names: Record<string, string>,
}

export const apiGetPrepaidCompetitionConfig = async () => get('/incomes/prepaid-competition/config') as Promise<PrepaidCompetitionConfigData>
// On-demand: slow (a logged-in Eventor Order-module scrape). Only call when the selector opens.
export const apiGetPrepaidCompetitionPaymentEvents = async () =>
    get(`/incomes/prepaid-competition/payment-events?x=1`) as Promise<{ payment_events: PaymentEvent[] }>
export const apiSetPrepaidCompetitionScalars = async (scalars: PrepaidCompetitionScalars) => put('/incomes/prepaid-competition/config/scalars', scalars) as Promise<{}>
export const apiPutPrepaidCompetitionPrice = async (row: PrepaidCompetitionPriceLocation) => put('/incomes/prepaid-competition/config/price', row) as Promise<{}>
export const apiDeletePrepaidCompetitionPrice = async (unit_price: number) => del('/incomes/prepaid-competition/config/price', { unit_price }) as Promise<{}>

export const apiGetEventorMembers = async () => get('/imports/eventormembers') as Promise<BasicMemberInfo[]>

export const apiSaveExpenseDraft = async (draft: DraftToSave) => put('/expenses/draft', draft) as Promise<{}>
// Take back the acting role's own signature. Refused once the draft is fully attested, since that
// is when it was booked — the answer is then a 409 the error bus shows.
export const apiRevokeAttest = async (draft_id: string) =>
    del('/expenses/attest', { draft_id }) as Promise<ExpensesData>

export const apiSaveAndAttestExpenseDraft = async (draft: DraftToSave) => put('/expenses/saveandattest', draft) as Promise<ExpensesData>

export const apiAttestExpenseDraft = async (data: {draft_id: string, version: number}) => put('/expenses/attest', data) as Promise<ExpensesData>

export const apiGetExpensesData = async () => get('/expenses/react/everything') as Promise<ExpensesData>

export const apiGetOverviewTree = async () =>
    get('/overview') as Promise<{ cost_center_items: CostCenterItem[], all_scopes: boolean }>

export const apiSetOverviewAllScopes = async (value: boolean) =>
    put('/overview/all-scopes', { value }) as Promise<{}>

export const apiGetOverview = async (data: {cost_center_item_id: string, project_id: string}) =>
    post('/overview/content', data) as Promise<OverviewData>

// The kostnadsställe's budget and result for the year. Separate from the rows because it needs no
// project — asking for one before showing it made the reader choose something irrelevant.
export const apiGetCostCenterStatus = async (costCenterItemId: string) => {
    const query = `cost_center_item_id=${encodeURIComponent(costCenterItemId)}`
    return get(`/overview/cost-center-status?${query}`) as Promise<{ budget: BudgetStatus | null }>
}

export const apiSendReminder = async (data: {customer_invoice_id: string}) => post('/overview/reminder', data) as Promise<{}>

// --- Budget: the expected net result per kostnadsställe per verksamhetsår ---
export type FiscalYear = { start: string, end: string, label: string, is_current: boolean }
export type BudgetCostCenterItem = { cost_center_item_id: string, short_name: string, name: string }
export type BudgetData = {
    fiscal_years: FiscalYear[],
    cost_center_items: BudgetCostCenterItem[],
    // fiscal year start -> cost centre id -> amount. Every year at once: switching year is then
    // instant, and the picker can show which years have been budgeted at all.
    budgets: Record<string, Record<string, number>>,
}

export const apiGetBudget = async () => get('/admin/budget') as Promise<BudgetData>

// A null amount clears the budget, which is not the same as budgeting zero.
export const apiSetBudget = async (fiscal_year_start: string, cost_center_item_id: string, amount: number | null) =>
    put('/admin/budget', { fiscal_year_start, cost_center_item_id, amount }) as Promise<{}>

export const apiSetResponsible = async (data: {draft_id: string, responsible: Responsible}) => put('/expenses/responsible', data) as Promise<{}>
export const apiSetPrepareAllScopes = async (value: boolean) => put('/expenses/prepare-all-scopes', {value}) as Promise<{}>

export const apiGetSwishSettings = async () => get('/swish/settings') as Promise<SwishSettingsData>

export const apiSetSwishPosMessage = async (pos_message: string | null) =>
    put('/swish/settings/pos-message', { pos_message }) as Promise<{}>

export const apiPutSwishCategory = async (mapping: SwishCategoryMapping) => put('/swish/settings/category', mapping) as Promise<{}>

export const apiDeleteSwishCategory = async (category_uuid: string) => del('/swish/settings/category', { category_uuid }) as Promise<{}>

export const apiPutSwishMessage = async (mapping: SwishMessageMapping) => put('/swish/settings/message', mapping) as Promise<{}>

export const apiDeleteSwishMessage = async (message: string) => del('/swish/settings/message', { message }) as Promise<{}>

// The poster PDF is a plain GET (read-only) the browser opens/downloads directly.
export const swishPosterUrl = (message: string) => `/swish/settings/message/poster?message=${encodeURIComponent(message)}`

export const apiPutSwishDefault = async (location: SwishLocation) => put('/swish/settings/default', location) as Promise<{}>

export const apiDeleteSwishDefault = async () => del('/swish/settings/default', {}) as Promise<{}>

export const apiPutSwishGiftcardCost = async (location: SwishCostLocation) =>
    put('/swish/settings/giftcard-cost', location) as Promise<{}>

export const apiDeleteSwishGiftcardCost = async () => del('/swish/settings/giftcard-cost', {}) as Promise<{}>

export const apiGetSwishSyncDefaults = async () =>
    get('/swish/sync/defaults') as Promise<{ from_date: string | null, cutoff: string }>

export const apiGetSwishSyncPreview = async (cutoff?: string, fromDate?: string) => {
    const params = new URLSearchParams()
    if (fromDate) params.set('from_date', fromDate)
    if (cutoff) params.set('cutoff', cutoff)
    const qs = params.toString()
    return get('/swish/sync/preview' + (qs ? `?${qs}` : '')) as Promise<SwishSyncPreview>
}

export const apiCommitSwishSync = async (cutoff: string, fromDate: string | undefined, rail: SyncRail) =>
    post('/swish/sync/commit', fromDate ? { cutoff, from_date: fromDate, rail } : { cutoff, rail }) as Promise<{ booked: string[] }>

// --- Eko user -> Eventor person (see web/admin.py) ---
//
// Who each Eko user is in Eventor. An administrator decides it; the connection is what lets Eko
// open Eventor already signed in as the person working, instead of as whoever last used the tab.
export type EventorPersonUser = {
    id: string, name: string, email: string,
    person_id: string | null, assigned_by: string | null, assigned_at: string | null,
}
export type ClubPerson = { person_id: string, name: string, year_of_birth: number | null }

export const apiGetEventorPersons = async () =>
    get('/admin/eventor-persons') as Promise<{ users: EventorPersonUser[], persons: ClubPerson[] }>

// `person_id: null` disconnects, which takes effect at once: the next attempt to open Eventor
// signed in has nobody to open it as.
export const apiSetEventorPerson = async (user_id: string, person_id: string | null) =>
    post('/admin/eventor-persons', { user_id, person_id }) as Promise<{}>

// --- Eko-hämtaren: fetching Eventor through the user's own browser (see web/courier.py) ---
//
// Eventor sits behind a Cloudflare challenge that nothing on the server can pass, so the fetching
// happens in the browser of whoever is signed in to Eventor. Eko starts a job, opens an Eventor tab
// carrying the job token in the URL fragment, and the bookmarklet does the rest.
export type CourierJob = { token: string, eventor_url: string }

export type Bookmarklet = { href: string, version: string }
// A one-shot link into a signed-in Eventor, for the tab Eko opens. `url` is null when nobody has
// connected this user to an Eventor person, or when Eventor would not mint one — both meaning
// "open Eventor the ordinary way and let them sign in". Never logged, never stored: for five
// minutes, whoever holds it is that person.
export const apiEventorLoginUrl = async () =>
    post('/courier/eventor-login-url', {}) as Promise<{ url: string | null, reason: string | null }>

export const apiGetBookmarklet = async () => get('/courier/bookmarklet') as Promise<Bookmarklet>

export const apiCourierLive = async () =>
    get('/courier/live') as Promise<{ live: boolean, stale: boolean, eventor_url: string,
        // Whether an administrator has connected this user to an Eventor person, and Eko can
        // therefore open the tab signed in.
        can_sign_in: boolean }>


export const apiGetCompany = async () => get('/auth/company') as Promise<{id: string, name: string}>

export type Role = { id: number, name: string }

export const apiGetRoles = async () => get('/auth/roles') as Promise<{roles: Role[], active_role_id: number | null, capabilities: Capability[], scope: [string, string][], prepare_all_scopes: boolean, user_name: string | null}>

export const roleSelectUrl = (roleId: number) => `/auth/roles/${roleId}/select`

export const apiLogOut = async () => get('/auth/logout') as Promise<{}>

// Pushes the idle deadline out and does nothing else — what "Stanna kvar" in the expiry warning
// sends. Every request refreshes the window; this is just the cheapest one to make. A session
// that is already gone answers 401, which the error bus turns into the login screen.
export const apiKeepalive = async () => get('/auth/keepalive') as Promise<{}>

// --- Admin: role/scope/capability editor ---
export type AdminCostCenterItem = { id: string, short_name: string, name: string }
export type AdminProject = { id: string, number: string, name: string }
export type AdminUser = { id: string, email: string, name: string }
export type AdminScopePair = { cost_center_item_id: string, project_id: string }
export type AdminRole = { id: number, name: string, capabilities: Capability[], user_ids: string[], scope: AdminScopePair[] }
export type AdminCapabilityInfo = { capability: Capability, kind: 'scoped' | 'company' | 'admin' }
export type AdminData = {
    roles: AdminRole[],
    cost_center_items: AdminCostCenterItem[],
    projects: AdminProject[],
    users: AdminUser[],
    capabilities: AdminCapabilityInfo[],
}

export const apiGetAdminData = async () => get('/admin/everything') as Promise<AdminData>
export const apiCreateRole = async (name: string) => post('/admin/roles', { name }) as Promise<{}>
export const apiDestroyRole = async (name: string) => del('/admin/roles', { name }) as Promise<{}>
export const apiGrantCapability = async (role_name: string, capability: Capability) => post('/admin/capability', { role_name, capability }) as Promise<{}>
export const apiRevokeCapability = async (role_name: string, capability: Capability) => del('/admin/capability', { role_name, capability }) as Promise<{}>
export const apiAddScope = async (role_name: string, cost_center_item_id: string, project_id: string) => post('/admin/scope', { role_name, cost_center_item_id, project_id }) as Promise<{}>
export const apiRemoveScope = async (role_name: string, cost_center_item_id: string, project_id: string) => del('/admin/scope', { role_name, cost_center_item_id, project_id }) as Promise<{}>
export const apiSetScopeProjects = async (role_name: string, cost_center_item_id: string, project_ids: string[]) => post('/admin/scope/bulk', { role_name, cost_center_item_id, project_ids }) as Promise<{}>
export const apiAddUserRole = async (role_name: string, user_id: string) => post('/admin/user-role', { role_name, user_id }) as Promise<{}>
export const apiRemoveUserRole = async (role_name: string, user_id: string) => del('/admin/user-role', { role_name, user_id }) as Promise<{}>

// --- Admin: company-wide chart config (aliases + account↔project) ---
export type AdminAccount = { number: number, name: string }
// Chart accounts also carry what kind they are, decided by common/utilities.py from the number.
export type AccountKind = 'income' | 'expense' | 'balance'
export type AdminChartAccount = AdminAccount & { type: AccountKind }
export type AdminAlias = { account_number: number, display_name: string }
export type AdminAccountProject = { account_number: number, project_id: string }
// The Swish scalars (formerly swish.json). null when the company hasn't configured Swish yet.
export type SwishScalars = {
    bank_account: number, clearing_account: number, clearing_voucher_series: string,
    recognition_voucher_series: string | null, swish_number: string | null,
}
export type NotificationConfig = {
    enabled: boolean, reminder_interval_days: number, due_soon_days: number,
}
export type AdminConfigData = {
    projects: AdminProject[],
    accounts: AdminChartAccount[],
    bank_accounts: AdminAccount[],
    aliases: AdminAlias[],
    account_project: AdminAccountProject[],
    swish_scalars: SwishScalars | null,
    notifications: NotificationConfig | null,
    // Ledger account number foreign supplier invoices are paid from; null until chosen (no
    // foreign supplier can be created before then — Visma requires it).
    foreign_pay_from_account: number | null,
    // The expense account Zettle's card fee is booked to; null until chosen.
    zettle_card_fee_account: number | null,
    // The cash account the till's kontant takings are debited to; null until chosen.
    zettle_cash_account: number | null,
    // The bank's Swish fee: öre per received payment (null = not booked), the expense account it
    // is booked to and the skuldkonto it is owed on until the bank draws it.
    swish_fee: SwishFeeSettings,
    // The club card's last four digits, or null while nobody has said. Null is unknown, not
    // "no card": nothing is guessed about who paid until it is set.
    card_last_four: string | null,
    // The roles that see underlag nobody holds yet (empty = every role that may attest), and the
    // roles that can be chosen.
    intake_role_ids: number[],
    attesting_roles: { id: number, name: string }[],
}

export const apiSetIntakeRoles = async (role_ids: number[]) =>
    post('/admin/config/intake-roles', { role_ids }) as Promise<{}>

export const apiGetAdminConfig = async () => get('/admin/config') as Promise<AdminConfigData>
export const apiSetSwishScalars = async (scalars: SwishScalars) => post('/admin/config/swish-scalars', scalars) as Promise<{}>
export const apiSetForeignPayFromAccount = async (pay_from_account: number) =>
    post('/admin/config/foreign-pay-from-account', { pay_from_account }) as Promise<{}>
export const apiSetZettleCardFeeAccount = async (card_fee_account: number) =>
    post('/admin/config/zettle-card-fee-account', { card_fee_account }) as Promise<{}>
export const apiSetZettleCashAccount = async (cash_account: number) =>
    post('/admin/config/zettle-cash-account', { cash_account }) as Promise<{}>
export type SwishFeeSettings = { ore_per_payment: number | null, account: number | null, liability_account: number | null }
// Any subset: each setting saves on its own.
export const apiSetSwishFee = async (settings: Partial<SwishFeeSettings>) =>
    post('/admin/config/swish-fee', settings) as Promise<{}>
export const apiSetCardLastFour = async (last_four: string) =>
    post('/admin/config/card', { last_four }) as Promise<{}>
export const apiClearCardLastFour = async () => del('/admin/config/card', {}) as Promise<{}>
export const apiSetNotificationConfig = async (config: NotificationConfig) => post('/admin/config/notifications', config) as Promise<{}>
export const apiSetAlias = async (account_number: number, display_name: string) => post('/admin/config/alias', { account_number, display_name }) as Promise<{}>
export const apiRemoveAlias = async (account_number: number) => del('/admin/config/alias', { account_number }) as Promise<{}>
export const apiAddAccountToProject = async (account_number: number, project_id: string) => post('/admin/config/account-project', { account_number, project_id }) as Promise<{}>
export const apiRemoveAccountFromProject = async (account_number: number, project_id: string) => del('/admin/config/account-project', { account_number, project_id }) as Promise<{}>
